import React from 'react';
import { Tractor, Calculator, Landmark, Map } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета субсидий
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  farmTypes,
  regions
}) => {
  // Расчет текущего размера хозяйства
  const farmSize = parseFloat(formData.farmSize) || 0;
  
  // Расчет текущего периода в годах
  const programDuration = parseFloat(formData.programDuration) || 0;
  
  // Обработчик изменения размера хозяйства
  const handleFarmSizeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      farmSize: newValue
    }));
  };
  
  // Обработчик изменения срока программы
  const handleProgramDurationChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      programDuration: newValue
    }));
  };
  
  // Обработчик изменения типа хозяйства
  const handleFarmTypeChange = (farmTypeId) => {
    setFormData(prev => {
      const newState = { ...prev, farmType: farmTypeId };
      
      // Также обновляем количество сотрудников на основе типа хозяйства
      const selectedFarmType = farmTypes.find(type => type.id === farmTypeId);
      if (selectedFarmType) {
        newState.employeeCount = selectedFarmType.defaultEmployeeCount.toString();
      }
      
      return newState;
    });
  };
  
  // Обработчик изменения региона
  const handleRegionChange = (regionCode) => {
    setFormData(prev => ({
      ...prev,
      regionCode
    }));
  };
  
  // Предопределенные размеры хозяйства для быстрого выбора
  const predefinedSizes = [
    { value: '10', label: '10 га' },
    { value: '100', label: '100 га' },
    { value: '500', label: '500 га' },
    { value: '1000', label: '1000 га' }
  ];
  
  // Предопределенные сроки программы для быстрого выбора
  const predefinedDurations = [
    { value: '1', label: '1 год' },
    { value: '3', label: '3 года' },
    { value: '5', label: '5 лет' },
    { value: '7', label: '7 лет' }
  ];
  
  // Предопределенные типы хозяйств для быстрого выбора (возьмем первые 4)
  const predefinedFarmTypes = farmTypes.slice(0, 4);
  
  // Предопределенные регионы для быстрого выбора (возьмем первые 4 + случайные из списка)
  const predefinedRegions = [
    regions[0], // Москва
    regions[2], // Московская область
    regions[3], // Краснодарский край
    regions[7]  // Республика Башкортостан
  ];
  
  // Функция расчета
  const calculateSubsidies = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Вспомогательная функция для склонения слов
  const pluralize = (number, one, few, many) => {
    let n = Math.abs(number);
    n %= 100;
    if (n >= 5 && n <= 20) {
      return many;
    }
    n %= 10;
    if (n === 1) {
      return one;
    }
    if (n >= 2 && n <= 4) {
      return few;
    }
    return many;
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Размер хозяйства */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="farmSizeSlider" className="text-gray-600 dark:text-gray-400">Размер хозяйства</label>
            <span className="font-medium text-gray-900 dark:text-white">{farmSize} га</span>
          </div>
          <input
            type="range"
            id="farmSizeSlider"
            min="1"
            max="2000"
            step="10"
            value={farmSize}
            onChange={handleFarmSizeChange}
            aria-label="Размер хозяйства"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными размерами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedSizes.map(size => (
              <button
                key={size.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, farmSize: size.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.farmSize === size.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {size.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок программы */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="programDurationSlider" className="text-gray-600 dark:text-gray-400">Срок программы</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {programDuration} {pluralize(programDuration, 'год', 'года', 'лет')}
            </span>
          </div>
          <input
            type="range"
            id="programDurationSlider"
            min="1"
            max="10"
            step="1"
            value={programDuration}
            onChange={handleProgramDurationChange}
            aria-label="Срок программы (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedDurations.map(duration => (
              <button
                key={duration.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, programDuration: duration.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.programDuration === duration.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {duration.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа хозяйства */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип хозяйства</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {predefinedFarmTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleFarmTypeChange(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.farmType === type.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium flex items-center justify-center">
                  <Landmark className="w-4 h-4 mr-1" />
                  {type.name}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор региона */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Регион</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {predefinedRegions.map(region => (
              <button
                key={region.code}
                type="button"
                onClick={() => handleRegionChange(region.code)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.regionCode === region.code 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium flex items-center justify-center">
                  <Map className="w-4 h-4 mr-1" />
                  {region.name}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Включение/отключение программ субсидирования */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Программы субсидирования</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, includeCapitalGrants: !prev.includeCapitalGrants }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.includeCapitalGrants 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Капитальные гранты</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, includeOperationalSubsidies: !prev.includeOperationalSubsidies }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.includeOperationalSubsidies 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Операционные субсидии</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, includeLoanSubsidies: !prev.includeLoanSubsidies }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.includeLoanSubsidies 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Субсидия по кредитам</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, includeExportSupport: !prev.includeExportSupport }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.includeExportSupport 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Поддержка экспорта</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateSubsidies}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать субсидии
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;